import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const customError = location.state && location.state.error;
  const [localError, setLocalError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const loggedUser = await login(email, password);
      if (loggedUser) {
        if (loggedUser.admin) {
          navigate('/panel-administrador/generar-token');
        } else {
          navigate('/');
        }
      }
      setLocalError(null);
    } catch (err) {
      // Mostrar mensaje de error personalizado
      setLocalError(err.response?.data?.message || 'Usuario o contraseña incorrectos');
    }
  };

  // Limpiar el error personalizado después de 5 segundos
  useEffect(() => {
    if (customError || localError) {
      const timer = setTimeout(() => {
        setLocalError(null);
        navigate('/login', { replace: true, state: {} });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [customError, localError, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8 px-2 sm:px-4 md:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8 bg-white rounded-lg shadow-lg p-4 sm:p-8">
        <div>
          <div className="mx-auto h-24 w-24 sm:h-36 sm:w-36 flex items-center justify-center rounded-full bg-blue-500">
            <LockClosedIcon className="h-12 w-12 sm:h-20 sm:w-20 text-white" />
          </div>
          <h2 className="mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
            VML Technologies
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Inicia sesión para acceder a la plataforma
          </p>
        </div>

        { (error || customError || localError) && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{localError || customError || error}</p>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">Correo electrónico</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="w-full rounded-md border-gray-400 shadow-sm focus:border-indigo-500 focus:ring-indigo-400 mb-4 text-base sm:text-lg"
                placeholder="Correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Contraseña</label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="w-full rounded-md border-gray-400 shadow-sm focus:border-indigo-500 focus:ring-indigo-400 text-base sm:text-lg pr-10"
                  placeholder="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="w-full mt-4 rounded-md border border-transparent shadow-sm px-4 py-2 text-md font-medium text-white bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Iniciar Sesión
            </button>
          </div>
        </form>
        {/* Footer con banner de holdings */}
        <div className="mt-8 flex justify-center">
          <div className="rounded-lg p-2 w-full flex justify-center">
            <img
              src='/assets/holdingCompanies.png'
              alt="Empresas del holding"
              className="object-contain w-full h-10 sm:h-12"
              style={{ maxWidth: '100%', filter: 'invert(1)' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 